import React, { Component } from "react";
import ReactGA from "react-ga";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import BannerImg from "../assets/images/banner/it-consulting.png";
import JsonLd from "../components/seo/json-ld";

const focusAreas = [
  {
    title: "Legacy system augmentation",
    text:
      "Add intelligence to existing enterprise platforms without forcing a replacement program or breaking working business rules.",
  },
  {
    title: "Workflow automation",
    text:
      "Use AI where repetitive operational steps, document handling and routing can be improved inside current processes.",
  },
  {
    title: "Decision support and governance",
    text:
      "Keep human accountability in the loop while AI helps teams interpret data, summarise activity and surface exceptions.",
  },
];

const deliveryPatterns = [
  {
    title: "Enterprise modernization",
    text:
      "Enterprise AI fits best when it sits alongside modernization work that is already improving the application landscape.",
  },
  {
    title: "Integration and orchestration",
    text:
      "AI becomes practical when the data and system integration layer is already stable and maintainable.",
  },
  {
    title: "Operational software",
    text:
      "Business-critical workflows need AI that supports operations rather than distracting from them.",
  },
];

const evidenceLinks = [
  { label: "Managed Services", href: "/services/managed-services", text: "Support, maintenance and controlled upgradation." },
  { label: "Dedicated Engineering Teams", href: "/services/dedicated-engineering-teams", text: "Embedded capacity for complex delivery." },
  { label: "Legacy Modernization", href: "/services/legacy-modernization", text: "Preserve and modernize safely." },
  { label: "Enterprise AI Enablement", href: "/services/enterprise-ai-enablement", text: "A service layer for governed AI adoption." },
  { label: "DXchange", href: "/dxchange", text: "Integration and enterprise data exchange." },
  { label: "BizRuleEngine", href: "/biz-rule-engine", text: "Rules-driven workflow orchestration." },
  { label: "CMS", href: "/cms", text: "Content and document platform heritage." },
  { label: "Travel & Hospitality", href: "/travel-hospitality", text: "Operational systems and AI layering." },
  { label: "Healthcare", href: "/healthcare", text: "Workflow-heavy enterprise delivery." },
  { label: "Insurance", href: "/insurance", text: "Regulated systems and decision support." },
  { label: "Legal", href: "/legal", text: "Document-intensive enterprise workflows." },
];

class EnterpriseAI extends Component {
  componentDidMount() {
    ReactGA.initialize("UA-5235714-2");
    ReactGA.pageview(window.location.pathname + window.location.search);
  }

  render() {
    const schemas = [
      {
        "@context": "https://schema.org",
        "@type": "WebPage",
        name: "Enterprise AI Enablement, Legacy Modernization & Decision Support | Avani Cimcon",
        url: "https://www.avanicimcon.com/enterprise-ai",
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: "https://www.avanicimcon.com/" },
          { "@type": "ListItem", position: 2, name: "Enterprise AI", item: "https://www.avanicimcon.com/enterprise-ai" },
        ],
      },
    ];

    return (
      <React.Fragment>
        <Helmet defer={false}>
          <title>Enterprise AI Enablement, Legacy Modernization & Decision Support | Avani Cimcon</title>
          <meta
            name="description"
            content="Enterprise AI enablement for mission-critical systems. Avani Cimcon layers AI onto legacy platforms, workflows and integration layers with delivery discipline."
          />
          <meta
            name="keywords"
            content="enterprise ai, AI enablement, legacy modernization, enterprise software engineering, workflow automation, decision support, AI strategy, AI integration, AI governance, enterprise delivery"
          />
          <link rel="canonical" href="https://www.avanicimcon.com/enterprise-ai" />
          <meta property="og:type" content="website" />
          <meta property="og:title" content="Enterprise AI Enablement, Legacy Modernization & Decision Support | Avani Cimcon" />
          <meta
            property="og:description"
            content="Enterprise AI enablement for mission-critical systems. Avani Cimcon layers AI onto legacy platforms, workflows and integration layers with delivery discipline."
          />
          <meta property="og:image" content="https://www.avanicimcon.com/images/banner/it-consulting.png" />
          <meta name="twitter:card" content="summary_large_image" />
          <meta
            name="twitter:title"
            content="Enterprise AI Enablement, Legacy Modernization & Decision Support | Avani Cimcon"
          />
          <meta
            name="twitter:description"
            content="Enterprise AI enablement for mission-critical systems. Avani Cimcon layers AI onto legacy platforms, workflows and integration layers with delivery discipline."
          />
          <meta name="twitter:image" content="https://www.avanicimcon.com/images/banner/it-consulting.png" />
        </Helmet>
        <JsonLd data={schemas} />

        <main className="industry-page enterprise-ai-page">
          <section className="industry-hero" aria-labelledby="enterprise-ai-hero-title">
            <div className="container">
              <div className="industry-hero-grid">
                <div className="industry-hero-copy">
                  <span className="section-eyebrow">Enterprise AI</span>
                  <h1 id="enterprise-ai-hero-title">Enterprise AI Enablement</h1>
                  <p className="industry-hero-lead">
                    Avani Cimcon applies AI where it can strengthen existing enterprise systems,
                    improve operational workflows and support modernization without disrupting
                    the business logic already in place.
                  </p>
                  <div className="industry-hero-tags" aria-label="Enterprise AI focus areas">
                    <span>Legacy systems</span>
                    <span>Workflow automation</span>
                    <span>Integration layers</span>
                    <span>Decision support</span>
                  </div>
                  <div className="industry-hero-actions">
                    <Link to="/contact-us" className="hero-v2-btn-primary">
                      Talk to Us
                    </Link>
                    <Link to="/services/enterprise-ai-enablement" className="hero-v2-btn-outline">
                      See Enterprise AI Enablement
                    </Link>
                  </div>
                </div>

                <div
                  className="industry-hero-panel"
                  style={{ "--industry-hero-image": `url(${BannerImg})` }}
                  aria-hidden="true"
                >
                  <div className="industry-hero-panel-scrim" />
                  <div className="industry-hero-panel-inner">
                    <p className="industry-hero-panel-kicker">AI as an enterprise delivery layer.</p>
                    <h2>AI belongs inside governed systems, not outside them.</h2>
                    <p>
                      The hub exists to show where enterprise AI can add value across existing
                      platforms, delivery models and vertical use cases already present on the
                      site.
                    </p>
                    <div className="industry-hero-stats">
                      <div className="industry-hero-stat">
                        <strong>Governance</strong>
                        <span>Human accountable delivery</span>
                      </div>
                      <div className="industry-hero-stat">
                        <strong>Integration</strong>
                        <span>AI fits the data layer</span>
                      </div>
                      <div className="industry-hero-stat">
                        <strong>Outcomes</strong>
                        <span>Practical support for operations</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="industry-section">
            <div className="container">
              <div className="industry-section-heading">
                <p className="section-eyebrow">Opportunity Areas</p>
                <h2 className="section-title">Where enterprise AI can create value first</h2>
                <p className="section-sub">
                  Start with the systems already in use, then extend them with intelligence where
                  the operational benefit is clear and measurable.
                </p>
              </div>
              <div className="row">
                {focusAreas.map((item) => (
                  <div key={item.title} className="col-lg-4 mb-4">
                    <article className="industry-card">
                      <h3>{item.title}</h3>
                      <p>{item.text}</p>
                    </article>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="industry-section industry-section--muted">
            <div className="container">
              <div className="industry-split">
                <div>
                  <p className="section-eyebrow">Delivery Experience</p>
                  <h2 className="section-title">Enterprise AI works best when the delivery model is already strong</h2>
                  <p className="section-sub" style={{ textAlign: "left", marginLeft: 0 }}>
                    The experience below is drawn from the existing enterprise software context on
                    the site. It reinforces how AI should be layered into systems that already
                    handle modernization, integration and operational software.
                  </p>
                  <div className="industry-copy-card">
                    <p>
                      This experience includes enterprise modernization, workflow automation,
                      legacy modernization, integration, distributed systems, multilingual systems,
                      cloud migration, enterprise architecture and operational software.
                    </p>
                    <p>
                      The goal is not to promote AI as a standalone product. The goal is to make AI
                      useful inside the operational reality that enterprises already trust.
                    </p>
                  </div>
                </div>
                <div className="industry-info-card">
                  <h3>Delivery patterns that matter</h3>
                  <ul className="industry-info-list">
                    {deliveryPatterns.map((item) => (
                      <li key={item.title}>
                        <strong>{item.title}</strong>
                        <span>{item.text}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </section>

          <section className="industry-section">
            <div className="container">
              <div className="industry-section-heading">
                <p className="section-eyebrow">Enterprise Delivery Evidence</p>
                <h2 className="section-title">The hub connects AI to the rest of the enterprise story</h2>
                <p className="section-sub">
                  These routes preserve the approved topical clusters while making the AI story
                  easier to discover from the pages that already hold authority.
                </p>
              </div>
              <div className="row">
                {evidenceLinks.map((item) => (
                  <div key={item.href} className="col-lg-3 col-md-6 mb-4">
                    <Link to={item.href} className="industry-link-card">
                      <h3>{item.label}</h3>
                      <p>{item.text}</p>
                    </Link>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="industry-section industry-section--muted">
            <div className="container">
              <div className="industry-section-heading">
                <p className="section-eyebrow">Why Avani Cimcon</p>
                <h2 className="section-title">Enterprise AI should reflect experience, expertise, authority and trust</h2>
                <p className="section-sub">
                  The hub reinforces EEAT by tying AI to the company’s long delivery history,
                  product heritage and domain-specific implementations.
                </p>
              </div>
              <div className="row">
                <div className="col-lg-3 col-md-6 mb-4">
                  <article className="industry-card">
                    <h3>Experience</h3>
                    <p>AI is presented as a layer on top of decades of enterprise software delivery.</p>
                  </article>
                </div>
                <div className="col-lg-3 col-md-6 mb-4">
                  <article className="industry-card">
                    <h3>Expertise</h3>
                    <p>The page connects AI to integration, modernization and operational engineering.</p>
                  </article>
                </div>
                <div className="col-lg-3 col-md-6 mb-4">
                  <article className="industry-card">
                    <h3>Authority</h3>
                    <p>Existing vertical and product pages continue to provide the supporting proof.</p>
                  </article>
                </div>
                <div className="col-lg-3 col-md-6 mb-4">
                  <article className="industry-card">
                    <h3>Trust</h3>
                    <p>AI is framed as a governed capability that supports business outcomes responsibly.</p>
                  </article>
                </div>
              </div>
            </div>
          </section>

          <section className="industry-cta">
            <div className="container">
              <div className="industry-cta-panel">
                <div>
                  <p className="section-eyebrow">Talk to Us</p>
                  <h2>Put Enterprise AI inside the systems your business already depends on</h2>
                  <p>
                    We can help you identify where AI belongs, where it should not, and how to
                    keep the delivery model aligned with enterprise operations.
                  </p>
                </div>
                <div className="industry-cta-actions">
                  <Link to="/contact-us" className="hero-v2-btn-primary">
                    Contact Us
                  </Link>
                  <Link to="/services/enterprise-ai-enablement" className="hero-v2-btn-outline">
                    Explore Enterprise AI Enablement
                  </Link>
                </div>
              </div>
            </div>
          </section>
        </main>
      </React.Fragment>
    );
  }
}

export default EnterpriseAI;
